import React, { memo, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import {
  CODE_TO_USAGE_TYPE,
  QUESTIONNAIRE_TITLE_EXTENSION_URL,
  QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL,
} from "src-next/modules/questionnaire-builder/form-builder/utils/questionnaireQuestionnaireConstants";
import type { QuestionnaireUsageTypeDomain } from "src-next/modules/patient-summary/transformers/extractQuestionnaireResponsesFromSummary";

type UsageTabValue = "all" | QuestionnaireUsageTypeDomain | "unclassified";

type QaPair = { question: string; answer: string };

type Row = {
  id: string;
  qr: fhir5.QuestionnaireResponse;
  title: string;
  usageDomain?: QuestionnaireUsageTypeDomain;
  usageLabel: string;
  authoredLabel: string;
  qaPairs: QaPair[];
};

const USAGE_TABS: { value: UsageTabValue; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "triage", label: "Triagem" },
  { value: "preConsultation", label: "Pré-consulta" },
  { value: "postConsultation", label: "Pós-consulta" },
  { value: "attendance", label: "Acompanhamento" },
  { value: "unclassified", label: "Sem classificação" },
];

const DATE_FMT = new Intl.DateTimeFormat("pt-BR", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

function formatAuthored(iso: string | undefined | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return DATE_FMT.format(d);
}

function usageFromQr(qr: fhir5.QuestionnaireResponse): {
  usageDomain?: QuestionnaireUsageTypeDomain;
  usageLabel: string;
} {
  const ext = qr.extension?.find(
    (e: any) => e?.url === QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL,
  ) as any;
  if (!ext) return { usageLabel: "—" };
  const codeRaw =
    ext?.valueCode != null && String(ext.valueCode).trim() !== ""
      ? String(ext.valueCode).trim()
      : ext?.valueCoding?.code != null &&
          String(ext.valueCoding.code).trim() !== ""
        ? String(ext.valueCoding.code).trim()
        : null;
  if (codeRaw) {
    const domain = CODE_TO_USAGE_TYPE[codeRaw] as
      | QuestionnaireUsageTypeDomain
      | undefined;
    if (domain) {
      const label = USAGE_TABS.find((t) => t.value === domain)?.label ?? codeRaw;
      return { usageDomain: domain, usageLabel: label };
    }
    return { usageLabel: codeRaw };
  }
  return { usageLabel: "—" };
}

function titleFromQr(qr: fhir5.QuestionnaireResponse): string {
  const titleExt = qr.extension?.find(
    (e: any) => e?.url === QUESTIONNAIRE_TITLE_EXTENSION_URL,
  ) as any;
  const fromExt = String(titleExt?.valueString ?? "").trim();
  if (fromExt) return fromExt;
  const ref =
    typeof qr.questionnaire === "string"
      ? qr.questionnaire
      : (qr.questionnaire as { reference?: string } | undefined)?.reference;
  const s = String(ref ?? "").trim();
  if (s.includes("/")) return s.split("/").pop() || s;
  return s || "Questionário";
}

function extractQaPairs(qr: fhir5.QuestionnaireResponse): QaPair[] {
  const pairs: QaPair[] = [];
  const walk = (items: fhir5.QuestionnaireResponseItem[] | undefined) => {
    for (const item of items ?? []) {
      if (item.item?.length) {
        walk(item.item);
        continue;
      }
      const answers = (item.answer ?? [])
        .map((answer) =>
          answer.valueString != null
            ? String(answer.valueString)
            : answer.valueBoolean != null
              ? answer.valueBoolean
                ? "Sim"
                : "Não"
              : answer.valueCoding
                ? String(
                    answer.valueCoding.display ?? answer.valueCoding.code ?? "",
                  )
                : "",
        )
        .filter((s) => s.length > 0);
      if (answers.length === 0 && !item.text) continue;
      pairs.push({
        question: item.text?.trim() || item.linkId?.trim() || "Pergunta",
        answer: answers.join(", ") || "—",
      });
    }
  };
  walk(qr.item);
  return pairs;
}

export interface VetPetQuestionnairesPanelProps {
  /**
   * Respostas já carregadas no contexto do atendimento (ex.: histórico do mesmo
   * `Questionnaire` de atendimento vet). Não inclui busca ampla por todos os
   * questionários do pet — isso exigiria endpoint/parametrização adicional.
   */
  questionnaireResponses: fhir5.QuestionnaireResponse[];
}

/**
 * Visualização compacta inspirada em `PatientQuestionnairesPanel`: abas por uso,
 * lista e detalhe Q/A. Tolerante a recursos sem extensão de uso (aba "Sem classificação").
 */
const VetPetQuestionnairesPanel = memo(
  ({ questionnaireResponses }: VetPetQuestionnairesPanelProps) => {
    const [usageTab, setUsageTab] = useState<UsageTabValue>("all");
    const [selected, setSelected] = useState<Row | null>(null);

    const rows = useMemo((): Row[] => {
      return questionnaireResponses.map((qr) => {
        const usage = usageFromQr(qr);
        const authored = String(qr.authored ?? "").trim();
        return {
          id: qr.id ?? `${titleFromQr(qr)}-${authored}`,
          qr,
          title: titleFromQr(qr),
          usageDomain: usage.usageDomain,
          usageLabel: usage.usageLabel,
          authoredLabel: formatAuthored(authored),
          qaPairs: extractQaPairs(qr),
        };
      });
    }, [questionnaireResponses]);

    const filtered = useMemo(() => {
      if (usageTab === "all") return rows;
      if (usageTab === "unclassified") {
        return rows.filter((r) => !r.usageDomain);
      }
      return rows.filter((r) => r.usageDomain === usageTab);
    }, [rows, usageTab]);

    if (!questionnaireResponses.length) {
      return (
        <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
          Nenhuma resposta de questionário carregada para este atendimento. O
          histórico exibido aqui reflete apenas os dados já buscados no fluxo do
          FluxVet (não é o sumário clínico completo do paciente humano).
        </Alert>
      );
    }

    return (
      <Stack spacing={2} sx={{ width: "100%" }}>
        {selected ? (
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={1}>
                <ListItemButton onClick={() => setSelected(null)} sx={{ py: 0 }}>
                  <Typography variant="body2" color="primary">
                    ← Voltar à lista
                  </Typography>
                </ListItemButton>
                <Typography variant="subtitle1" fontWeight={600}>
                  {selected.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selected.usageLabel} · {selected.authoredLabel}
                </Typography>
                <Divider />
                <Stack spacing={1}>
                  {selected.qaPairs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Sem perguntas/respostas aninhadas neste recurso.
                    </Typography>
                  ) : (
                    selected.qaPairs.map((pair, idx) => (
                      <Box key={`${pair.question}-${idx}`}>
                        <Typography variant="caption" color="text.secondary">
                          {pair.question}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          {pair.answer}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ) : (
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={2}>
                <Tabs
                  value={usageTab}
                  onChange={(_e, v: UsageTabValue) => setUsageTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {USAGE_TABS.map((tab) => (
                    <Tab
                      key={tab.value}
                      label={tab.label}
                      value={tab.value}
                      sx={{ textTransform: "none" }}
                    />
                  ))}
                </Tabs>
                <Divider />
                <List dense disablePadding sx={{ maxHeight: 280, overflow: "auto" }}>
                  {filtered.length === 0 ? (
                    <ListItem>
                      <ListItemText primary="Nenhum item nesta aba." />
                    </ListItem>
                  ) : (
                    filtered.map((row) => (
                      <ListItem key={row.id} disablePadding>
                        <ListItemButton onClick={() => setSelected(row)}>
                          <ListItemText
                            primary={row.title}
                            secondary={`${row.usageLabel} · ${row.authoredLabel}`}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))
                  )}
                </List>
              </Stack>
            </CardContent>
          </Card>
        )}
      </Stack>
    );
  },
);

VetPetQuestionnairesPanel.displayName = "VetPetQuestionnairesPanel";

export default VetPetQuestionnairesPanel;
